import { nowIso } from './domain/time.js';
/** Days a till keeps synced work when the server has not said otherwise. */
export const DEFAULT_LOCAL_RETENTION_DAYS = 7;
export const LOCAL_RETENTION_CONFIG_KEY = 'retention.localDays';
const DAY_MS = 24 * 60 * 60 * 1000;
export async function localRetentionDays(db) {
    const raw = await db.config.get(LOCAL_RETENTION_CONFIG_KEY);
    const days = raw === null ? NaN : Number(raw);
    return Number.isInteger(days) && days >= 1 ? days : DEFAULT_LOCAL_RETENTION_DAYS;
}
/** Records the server's retention setting, from a sync pull. */
export async function setLocalRetentionDays(db, days) {
    if (!Number.isInteger(days) || days < 1)
        return;
    await db.config.set(LOCAL_RETENTION_CONFIG_KEY, String(days));
}
/**
 * Removes what the server already has and the till no longer needs.
 *
 * Only ever synced work: an order is "synced" once every sync operation for
 * it is DONE (acknowledged by the server) and none is pending, sent,
 * rejected or in conflict - anything else stays, however old, because it is
 * the only copy. Open orders always stay. Unresolved conflicts always stay.
 * Safe to run at any time and any number of times.
 */
export async function pruneLocalData(db, now = new Date(), days) {
    const keepDays = days ?? (await localRetentionDays(db));
    const cutoff = new Date(now.getTime() - keepDays * DAY_MS).toISOString();
    const run = async (sql, params = []) => (await db.driver.execute({ sql, params })).changes;
    // 1. Mark what the server has acknowledged. Nothing set SYNCED before
    //    this, which is why pruneSynced() never removed anything.
    const allDone = `EXISTS (SELECT 1 FROM sync_queue q WHERE q.aggregate_id = orders.id AND q.state = 'DONE')
    AND NOT EXISTS (SELECT 1 FROM sync_queue q WHERE q.aggregate_id = orders.id AND q.state <> 'DONE')`;
    const markedSynced = await run(`UPDATE orders SET sync_state = 'SYNCED' WHERE sync_state <> 'SYNCED' AND status <> 'OPEN' AND ${allDone}`);
    await run(`UPDATE invoices SET sync_state = 'SYNCED'
      WHERE sync_state <> 'SYNCED' AND order_id IN (SELECT id FROM orders WHERE sync_state = 'SYNCED')`);
    // 2. Bills (lines cascade), then the orders behind them (lines and events cascade).
    const invoices = await db.invoices.pruneSynced(cutoff);
    const orders = await run(`DELETE FROM orders
      WHERE sync_state = 'SYNCED' AND status <> 'OPEN'
        AND COALESCE(closed_at, updated_at) < ?
        AND NOT EXISTS (SELECT 1 FROM invoices i WHERE i.order_id = orders.id)
        AND NOT EXISTS (SELECT 1 FROM sync_queue q WHERE q.aggregate_id = orders.id AND q.state <> 'DONE')`, [cutoff]);
    // 3. Acknowledged sync records, after the orders that needed them.
    const syncOperations = await db.syncQueue.pruneDone(cutoff);
    // 4. Housekeeping: ended or expired sessions, the local audit trail (the
    //    server's audit log is the record), resolved conflicts, old tombstones.
    const sessions = await run(`DELETE FROM local_session WHERE COALESCE(ended_at, expires_at) < ?`, [cutoff]);
    const auditEntries = await run(`DELETE FROM local_audit WHERE at < ?`, [cutoff]);
    const conflicts = await run(`DELETE FROM sync_conflicts WHERE resolved_at IS NOT NULL AND resolved_at < ?`, [cutoff]);
    const tombstones = await run(`DELETE FROM tombstones WHERE at < ?`, [cutoff]);
    return { markedSynced, invoices, orders, syncOperations, sessions, auditEntries, conflicts, tombstones };
}
/**
 * Takes offline sign-in away from everyone the server no longer lists for
 * this till - a removed membership, a deactivated account, a role that no
 * longer works the POS - on the first pull after the change, rather than
 * when their grant would have expired. Their open local session ends, their
 * PIN verifier, grant and cached permissions go, and their name and email
 * are cleared (the row stays: past orders refer to its id).
 *
 * Returns who was revoked, so the app can sign out a revoked user who is
 * using the till right now.
 */
export async function revokeUnlistedUsers(db, listedUserIds) {
    const listed = new Set(listedUserIds);
    const rows = await db.driver.query({
        sql: `SELECT id FROM users WHERE is_active = 1 OR offline_verifier IS NOT NULL OR grant_json IS NOT NULL OR name <> ''`,
    });
    const revoked = rows.map((row) => String(row.id)).filter((id) => !listed.has(id));
    if (revoked.length === 0)
        return [];
    const now = nowIso();
    await db.driver.batch(revoked.flatMap((id) => [
        {
            sql: `UPDATE users SET is_active = 0, offline_verifier = NULL, grant_json = NULL, grant_expires_at = NULL,
                     name = '', email = '' WHERE id = ?`,
            params: [id],
        },
        { sql: `UPDATE local_session SET ended_at = ? WHERE user_id = ? AND ended_at IS NULL`, params: [now, id] },
        { sql: `DELETE FROM local_config WHERE key = ? OR key = ?`, params: [`offline.permissions.${id}`, `offline.lockout.${id}`] },
    ]));
    return revoked;
}
//# sourceMappingURL=privacy.js.map